import { useApolloClient } from '@vue/apollo-composable'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { connectToSSE } from 'src/services/sse-service'
import { useSystemStore } from 'stores/system'
import { useSiteInfoStore } from 'stores/site-info'
import { useUserAuthStore } from 'stores/user/auth'
import { useUserBonusesStore } from 'stores/user/bonuses'
import { useModalsStore } from 'stores/modals'

import getUnderDev from 'src/apollo/queries/getUnderdev.gql'
import refreshToken from 'src/apollo/mutations/refreshToken.gql'
import { notification } from 'src/core/hooks/notifications'
import { actionToken, consoleLogAPI, defaultLang, renameKeys } from 'src/common/functions'
import { userService } from 'src/common/user-service'
import { BASE_URL, getCashBoxComponent } from 'src/common/config'
import restFormatToGqlFormat from 'src/apollo/restFormatToGqlFormat'
import { TokenService } from 'src/common/token-service'
import { computed } from 'vue'
import { useHelpers } from 'src/common/composition/useHelpers'
import { DOC_STATUSES } from 'src/common/dictionary'
import getBonusGameIds from 'src/apollo/queries/getBonusGameIds.gql'
import { debounce } from 'src/common/use-events-optimisation'
import emitter from 'src/services/event-emitter'
import { connectToWs } from 'src/services/websocket-service'

const tokenService = new TokenService()

export function usePollingUpdate() {
  const { t, locale } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const $helpers = useHelpers()

  const modalsStore = useModalsStore()
  const userAuthStore = useUserAuthStore()
  const userBonusesStore = useUserBonusesStore()
  const systemStore = useSystemStore()

  const { resolveClient } = useApolloClient()
  const apolloClient = resolveClient()

  const getUser = computed(() => userAuthStore.getUser)

  function openCashBox() {
    modalsStore.pushModal({ componentName: getCashBoxComponent() })
  }

  function notificationFn(content) {
    notification({
      type: getNotificationType(content.notification_type),
      text: content.text,
      timer: content.notification_time,
      canClose: content.notification_close,
    })
  }

  function logoutFn() {
    userAuthStore.removeToken()
    userService.logout().finally(() => {
      window.location.href = BASE_URL + '/' + locale === defaultLang ? '' : locale
    })
  }

  function deleteMessageFn() {
    //TODO store // в сторе нет этого экшена
  }

  function fetchBonusGameIds() {
    apolloClient
      .query({
        query: getBonusGameIds,
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        userBonusesStore.updateBonusGameIdsList(data?.userPromo?.activeStep?.gameIdsList ?? null)
      })
      .catch(consoleLogAPI)
  }

  const fetchBonusGameIdsDebounced = debounce(fetchBonusGameIds, 1500)

  const handlers = [
    // {
    //   message_type: '*',
    //   callback: (type, event) => {
    //     console.log(`ALL: ${new Date().toLocaleTimeString()}`, type, event)
    //   },
    // },
    {
      message_type: 'update_settings_underdev',
      callback: (content) => {
        if (systemStore.underdev !== content.underdev) {
          if (content.underdev) {
            notification({ type: 'warning', text: t('engineeringWorks') })

            setTimeout(() => {
              apolloClient
                .query({
                  query: getUnderDev,
                  fetchPolicy: 'network-only',
                })
                .then(({ data }) => {
                  if (data?.siteInfo?.underDev) {
                    systemStore.updateUnderDev(data.siteInfo.underDev)
                    router.push({ name: 'Home' })
                  }
                })
                .catch(consoleLogAPI)
            }, 300000)
          } else {
            location.reload()
          }
        }
      },
    },
    {
      message_type: 'token_will_expire',
      callback: async () => {
        const token = userAuthStore.getToken
        const actionTokenType = actionToken(token)

        if (actionTokenType === 'refresh') {
          await apolloClient
            .mutate({
              mutation: refreshToken,
              variables: { token },
            })
            .then(({ data }) => {
              userAuthStore.updateToken(data.refreshToken.token)
              tokenService.setToken(data.refreshToken.token)
            })
            .catch((e) => {
              consoleLogAPI(e)
              logoutFn()
            })
          return
        }

        if (actionTokenType === 'remove') {
          logoutFn()
        }
      },
    },
    {
      message_type: 'update_turnover',
      callback: (content) => {
        userAuthStore.updateUserField({ key: 'threshold', value: content })
      },
    },
    {
      message_type: 'update_fast_cashback',
      callback: (content) => {
        userAuthStore.updateCashback({
          amount: content.amount,
          spinsCount: content.spins_count,
        })
      },
    },
    {
      message_type: 'update_program',
      callback: (content) => {
        // отправляется при изменении Бонусной программы пользователям, у которых есть такой бонус
        userBonusesStore.updateProgram(content)
      },
    },
    {
      message_type: 'update_user_count_active_withdraws',
      callback: (content) => {
        userAuthStore.updateUserWithdrawalsRequests(content?.count_active_withdraw)
      },
    },
    {
      message_type: 'update_user_program',
      callback: (content) => {
        // отправляется пользователю при получении и активации бонуса
        userBonusesStore.updateUserProgram(renameKeys(content))
      },
    },
    {
      message_type: 'delete_user_program',
      callback: (content) => {
        userBonusesStore.deleteUserProgram(renameKeys(content))
      },
    },
    {
      message_type: 'update_user_step',
      callback: (content) => {
        const data = content ? renameKeys(content) : null
        if (data && [2, 4, 6].includes(data?.condition)) {
          fetchBonusGameIdsDebounced()
        }

        userBonusesStore.updateActiveStep(data)
        if (data?.propsContent[0]?.props?.casOpen?.value) {
          const action = data?.propsContent[0]?.props?.casOpen?.value

          if (action == 1 || (action == 2 && !systemStore.isMobile) || (action == 3 && systemStore.isMobile)) {
            openCashBox()
          }
        }
      },
    },
    {
      message_type: 'update_user_step_balance',
      callback: (content) => {
        // отправляется пользователю при смене поля balance в активном шаге
        userBonusesStore.updateUserStepBalance(content)
      },
    },
    {
      message_type: 'update_promo_freespin_available',
      callback: (content) => {
        const result = content?.map((i) => {
          return {
            id: i.id,
            activated: i.activated,
            game: {
              manufacturer: {
                code: i.game_obj.manufacturer_code,
              },
              slug: i.game_obj.slug,
              name: i.game_obj.name,
              compressedImage: i.game_obj.compressed_image,
            },
            campaignId: i.campaign_id,
            programId: i.program_id,
            freespinsCount: i.freespins_count,
            freespinsLeft: i.freespins_left,
            dateExpire: i.date_end,
          }
        })
        userBonusesStore.updatePromoFreespinAvailable(result || [])
      },
    },
    {
      message_type: 'promo_max_bet_exceeded',
      callback: (content) => {
        $helpers.notify({
          color: 'cas-err',
          type: 'negative',
          icon: 'las la-exclamation-triangle',
          message: t('promoMaxBetExceeded', {
            bet: content.max_bet,
            currency: useSiteInfoStore().userCurrency?.code,
          }),
        })
      },
    },
    {
      message_type: 'update_user_free_bets',
      callback: (content) => {
        userBonusesStore.updateFreeBets(content.count)
      },
    },
    {
      message_type: 'update_documents_status',
      callback: (content) => {
        const newDocsStatus = content.docs_verification

        if (userAuthStore.getUserVerificationStatus !== newDocsStatus) {
          if (newDocsStatus === DOC_STATUSES.VerifiedAndValid) {
            notification({ type: 'success', text: t('docsWasApproved') })
          }
          if (newDocsStatus === DOC_STATUSES.Declined) {
            notification({ type: 'warning', text: t('docsWasDeclined') })
          }
        }
        userAuthStore.updateUserFields({
          docsVerificationId: content.docs_verification,
          needVerification: content.need_verification,
          withdrawAccess: content.withdraw_access,
          withdrawAccessStr: content.withdraw_access_str,
        })
      },
    },
    {
      message_type: 'delete_message_for_all_users',
      callback: deleteMessageFn,
    },
    {
      message_type: 'delete_for_user',
      callback: deleteMessageFn,
    },
    {
      message_type: 'block_user',
      callback: logoutFn,
    },
    {
      message_type: 'throwout_user',
      callback: logoutFn,
    },
    {
      message_type: 'throwout_all_users',
      callback: logoutFn,
    },
    {
      message_type: 'notification_for_all_users',
      callback: notificationFn,
    },
    {
      message_type: 'notification_for_user',
      callback: notificationFn,
    },
    {
      message_type: 'update_balance',
      callback: (content) => {
        if (getUser.value.bonusBalanceOn !== content.bonus_balance_on) {
          const messageText = content.bonus_balance_on ? t('BonusBalanceOn') : t('BonusBalanceOff')
          const actions = [
            {
              label: content.bonus_balance_on ? t('play') : t('depositAccount'),
              handler: () => (content.bonus_balance_on ? router.push({ name: 'BonusGames' }) : openCashBox()),
            },
          ]
          notification({ text: messageText, timer: 4000, actions })
        }
        if (content.bonus_balance_on && route.name === 'Sports') {
          modalsStore.setSportModalStatus(true)
        }
        userAuthStore.updateUserFields({
          balance: content.balance,
          bonusBalance: content.bonus_balance,
          bonusBalanceOn: content.bonus_balance_on,
        })
      },
    },
    {
      message_type: 'update_balance_bk',
      callback: (content) => {
        userAuthStore.updateUserField({
          key: 'bkBalance',
          value: content.balance_bk,
        })
      },
    },
    {
      message_type: 'update_vager_info',
      callback: (content) => {
        userAuthStore.updateUserField({
          key: 'activeVager',
          value: content.active_vager,
        })
        userAuthStore.updateUserField({
          key: 'vagerBonus',
          value: content.vager_sum,
        })
      },
    },
    {
      message_type: 'update_user_info',
      callback: (content) => {
        if (getUser.value) {
          userAuthStore.updateUser(restFormatToGqlFormat(content))
        }
      },
    },
    {
      message_type: 'sign_out',
      callback: logoutFn,
    },
  ]

  // emitter.on('*', (type, event) => {
  //   console.log(`emitter.on ${type}` ,event)
  // })

  for (const item of handlers) {
    emitter.on(item.message_type, item.callback)
  }

  // const type = localStorage.getItem(SSE_USE_KEY) === '1' || route.query.sse === '1' ? 'sse' : null
  connectToServer()
}

function connectToServer(type = 'sse') {
  if (type === 'sse') {
    if (!window.EventSource) {
      console.warn('❌ SSE не поддерживается, переключаемся на WebSocket')
      connectToWs()
      return
    }

    connectToSSE()
    return
  }

  connectToWs()
}

function getNotificationType(notification_type) {
  switch (notification_type) {
    case 1:
      return 'info'
    case 2:
      return 'warning'
    case 3:
      return 'success'
    default:
      return 'info'
  }
}
